import { useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useEffect, useState, useCallback, useReducer } from 'react';
import { Node, ReactFlowProvider, useReactFlow } from 'reactflow';

import {
  artifactsDocumentToTapeBuilderGraph,
  tapeBuilderGraphToArtifactsDocument,
} from '@finley-technologies/tape-builder';

import { GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS } from '../requests/tapeBuilder.gql';
import { QAProvider } from './calcAgentQA/QAContext';
import { TapeBuilderDiffViewer } from './DiffViewer';
import {
  addIdsToEdges,
  getDefaultGraphState,
  GraphBasicState,
  GraphProvider,
  graphReducer,
  GraphState,
  OrgSlugToOrgMapType,
  StoredGraphState,
  TapeBuilderMode,
} from './GraphContext';
import { migrateGraph } from './migratexlsx2csv';
import { TapeBuilderDiffDocument } from './TapeBuilderDiffDocument';
import { TapeBuilderStatus } from './TapeBuilderStatus';
import { defaultToastOptions } from './utils';
import { GraphPositions } from 'packages/common-frontend/components/ReactFlow';
import { repositionGraph } from 'packages/common-frontend/components/ReactFlow/layout/d3hierarchy';
import {
  useGetPipelineInstancesLazyQueryGQLType,
  useGetBorrowingBasePipelineStatusLazyQueryGQLType,
  useDeleteBorrowingBasePipelineArtifactDocumentWipMutation,
  useSaveBorrowingBasePipelineArtifactDocumentWipMutation,
  useSetNodePositionsMutation,
  useGetGraphWithPositionsLazyQueryGQLType,
} from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';
import {
  toServerPositions,
  fromServerPositions,
} from 'packages/tapeBuilder/ui/normalize';
import { TapeBuilderControl } from 'packages/tapeBuilder/ui/TapeBuilderControl';

import './TapeBuilder.scss';

type Props = {
  pipelineDefinitionId: string;
  orgSlugMap: OrgSlugToOrgMapType;
};
const InternalComponent = ({
  pipelineDefinitionId, // @implementation: use this as primary query param.
  orgSlugMap,
}: Props) => {
  const reactFlowInstance = useReactFlow();
  const [mode, setMode] = useState<TapeBuilderMode>('Edit');
  const [diff, setDiff] = useState<TapeBuilderDiffDocument | null>(null);
  const getDefaultState = useCallback(() => getDefaultGraphState({ mode }), [
    mode,
  ]);

  const [graph, dispatch] = useReducer(graphReducer, getDefaultState());

  const updateFlowNodes = useCallback(
    ({ nodes, edges }: GraphBasicState, positions: GraphPositions) => {
      const needsRepositioning = nodes.some(
        (node) => positions[node.metadata.tapeBuilderNodeId] === undefined,
      );
      const flowNodes = needsRepositioning
        ? repositionGraph({
            nodes: nodes.map<Omit<Node, 'position'>>((node) => ({
              id: node.metadata.tapeBuilderNodeId,
              type: node.metadata.tapeBuilderNodeType,
              data: node.metadata.tapeBuilderNodeId,
            })),
            edges,
            xSpacing: 300,
            ySpacing: 300,
            disconnectedSpacing: 400,
          })
        : nodes.map<Node>((node) => ({
            id: node.metadata.tapeBuilderNodeId,
            type: node.metadata.tapeBuilderNodeType,
            data: node.metadata.tapeBuilderNodeId,
            position: positions[node.metadata.tapeBuilderNodeId]!,
          }));

      reactFlowInstance.setNodes(flowNodes);
      reactFlowInstance.setEdges(edges);
      reactFlowInstance.fitView();
    },
    [reactFlowInstance],
  );

  const setGraph = useCallback((newGraph: GraphState) => {
    dispatch({ type: 'setGraph', graph: newGraph });
  }, []);

  const clearGraph = () => {
    dispatch({ type: 'clearGraph' });
  };

  // artifact document to be displayed in the graph. (This could be different from the latestArtifactDocumentId)
  const [currentArtifactDocumentId, setCurrentArtifactDocumentId] = useState<
    string | null
  >(null);
  const toast = useToast(defaultToastOptions);

  const onError = useCallback(
    (error: any, prefix: string) => {
      // eslint-disable-next-line no-console
      console.error(prefix, error);
      const message = `${error}`;
      toast({
        id: `${Date.now()}`,
        title: message,
        status: 'error',
        duration: null, // errors are important, do not auto-close
      });
    },
    [toast],
  );

  const [
    queryGraphWithPositions,
    { loading: loadingCurrentArtifactDocument },
  ] = useGetGraphWithPositionsLazyQueryGQLType({
    onError: (err) => {
      onError(err, 'queryGraphWithPositions.onError');
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      try {
        const {
          graph: { nodes, edges },
          artifactDocumentId,
          dbView,
        } = artifactsDocumentToTapeBuilderGraph({
          artifactsDocument:
            data.borrowingBasePipelineArtifactDocumentInfo
              .artifactsDocumentString,
        });

        // mode need to be consistent with dbView.
        let newMode = mode;
        if ((mode === 'Monitor') !== dbView) {
          newMode = dbView ? 'Monitor' : 'Edit';
        }

        const edgesWithIDs = addIdsToEdges(edges);

        setGraph({
          ...graph,
          nodes,
          edges: edgesWithIDs,
          artifactDocumentId,
          dirty: false,
          mode: newMode,
        });

        const positions = fromServerPositions({
          dbPositions: data.nodePositions,
          nodes,
          edges,
        });

        updateFlowNodes({ nodes, edges: edgesWithIDs }, positions);
      } catch (error) {
        onError(error, 'queryGraphWithPositions.error.onCompleted');
      }
    },
  });

  const queryArtifactDocument = useCallback(
    (variables: { artifactDocumentId: string; dbView: boolean }) => {
      queryGraphWithPositions({
        variables: {
          ...variables,
          purpose: variables.dbView ? 'dataPipeline' : 'tapeBuilderEdit',
        },
      });
    },
    [queryGraphWithPositions],
  );

  const [
    setNodePositionsMutation,
    { loading: savingNodePositions },
  ] = useSetNodePositionsMutation({
    onError: (err) => onError(err, 'setNodePositionsMutation.onError'),
    onCompleted: () => {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: 'Graph Layout saved!',
        status: 'success',
        duration: 2000,
      });
    },
  });

  const saveNodePositions = useCallback(() => {
    // collect positions from react-flow.
    const flowPositions = reactFlowInstance
      .getNodes()
      .reduce<GraphPositions>((acc, node) => {
        // eslint-disable-next-line no-param-reassign
        acc[node.id] = node.position;
        return acc;
      }, {});

    const serverPosition = toServerPositions({
      positions: flowPositions,
      nodes: graph.nodes,
      edges: graph.edges,
    });

    // save the positions.
    setNodePositionsMutation({
      variables: {
        artifactDocumentId: graph.artifactDocumentId,
        positions: serverPosition,
        purpose: mode === 'Edit' ? 'tapeBuilderEdit' : 'dataPipeline',
      },
    });
  }, [
    reactFlowInstance,
    graph.nodes,
    graph.edges,
    graph.artifactDocumentId,
    setNodePositionsMutation,
    mode,
  ]);

  const [
    queryArtifactInstances,
    { loading: loadingArtifactInstances },
  ] = useGetPipelineInstancesLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data && data.getArtifactInstancesForPipelineRun) {
        const {
          artifactInstances,
          artifactDocumentId,
        } = data.getArtifactInstancesForPipelineRun;

        if (artifactDocumentId && mode === 'Monitor') {
          if (currentArtifactDocumentId !== artifactDocumentId) {
            setCurrentArtifactDocumentId(artifactDocumentId);
            queryArtifactDocument({
              artifactDocumentId,
              dbView: true,
            });
          }

          // see if we got any changes in status.
          const oldIdAndStatuses = graph.artifactInstances
            ? graph.artifactInstances
                .map(({ id, status }: { id: string; status: string }) => ({
                  id,
                  status,
                }))
                .sort((a: { id: string }, b: { id: string }) =>
                  a.id.localeCompare(b.id),
                )
            : [];
          const newIdAndStatuses = artifactInstances
            .map(({ id, status }: { id: string; status: string }) => ({
              id,
              status,
            }))
            .sort((a: { id: string }, b: { id: string }) =>
              a.id.localeCompare(b.id),
            );

          // update the state only if we got any new instances or statuses.
          // updating graph state here loses the selectedNode
          // state. we want to avoid that when there are no changes.
          if (!_.isEqual(oldIdAndStatuses, newIdAndStatuses)) {
            setGraph({
              ...graph,
              artifactInstances,
            });
          }
        } else if (graph.artifactInstances !== null) {
          setGraph({
            ...graph,
            artifactInstances: null,
          });
        }
      }
    },
    onError: (error) => {
      onError(error, 'queryArtifactInstances.onError');
    },
  });

  const [
    queryBorrowingBasePipelineStatus,
    { data: status, loading: loadingStatus },
  ] = useGetBorrowingBasePipelineStatusLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const creditFacilities = data.implementation.creditFacilities.map(
        ({ id, slug }) => ({
          id,
          slug,
        }),
      );
      const loanProducts = data.implementation.loanProducts.map(
        ({ id, slug }) => ({
          id,
          slug,
        }),
      );
      if (data.borrowingBasePipelineStatus.creditAgreementsNeedLink) {
        toast({
          status: 'warning',
          position: 'top',
          title: `Warning: Credit Agreements are not linked to latest committed tape-builder graph.  Please resolve any CA conflicts and re-commit CAs.`,
        });
      }
      setGraph({
        ...graph,
        // @implementation: we should get rid of this explicit reference to the borrower org
        orgSlug: data.implementation.borrowerOrganization.slug,
        creditFacilities,
        loanProducts,
      });

      setCurrentArtifactDocumentId(
        data.borrowingBasePipelineStatus.artifactDocumentWipId,
      );

      queryArtifactDocument({
        artifactDocumentId:
          data.borrowingBasePipelineStatus.artifactDocumentWipId,
        dbView: ['View', 'Monitor'].includes(mode),
      });
    },
    onError: (error) => {
      onError(error, 'queryBorrowingBasePipelineStatus.onError');
    },
  });

  const getStatus = useCallback(() => {
    queryBorrowingBasePipelineStatus({
      variables: { pipelineDefinitionId },
    });
  }, [queryBorrowingBasePipelineStatus, pipelineDefinitionId]);

  const getCurrentArtifactsDocument = useCallback(
    (newMode: TapeBuilderMode) => {
      // monitor mode always shows dbView.
      const dbView = ['View', 'Monitor'].includes(newMode);

      // fetch latest artifacts document when in edit/qa mode.
      const artifactDocumentId = dbView
        ? currentArtifactDocumentId
        : status?.borrowingBasePipelineStatus.artifactDocumentWipId;

      if (artifactDocumentId) {
        setCurrentArtifactDocumentId(artifactDocumentId);
        queryArtifactDocument({
          artifactDocumentId,
          dbView,
        });
      }
    },
    [
      currentArtifactDocumentId,
      queryArtifactDocument,
      status?.borrowingBasePipelineStatus.artifactDocumentWipId,
    ],
  );

  const loadArtifactInstances = useCallback(
    (pipelineRunId: string) => {
      // fetch artifact instances only when we are in Monitor mode.
      if (mode === 'Monitor') {
        queryArtifactInstances({
          variables: { pipelineRunId },
        });
      }
    },
    [queryArtifactInstances, mode],
  );

  // 1st time load the status.
  useEffect(() => {
    getStatus();
  }, [getStatus]);

  // mutation to save the borrowing base artifacts
  const [
    saveBorrowingBasePipelineArtifactsMutation,
    { loading: isSavingWip },
  ] = useSaveBorrowingBasePipelineArtifactDocumentWipMutation({
    onCompleted: (result) => {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: `graph saved: ${result.saveBorrowingBasePipelineArtifactDocumentWip.response}`,
        status: 'success',
      });

      getStatus();
    },
    onError: (error) => {
      onError(error, 'saveBorrowingBasePipelineArtifactsMutation.onError');
    },
    refetchQueries: [
      {
        query: GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS,
        variables: { pipelineDefinitionId },
      },
    ],
  });

  //
  const [
    resetArtifactDocumentWipToCurrent,
    { loading: isResettingToCurrent },
  ] = useDeleteBorrowingBasePipelineArtifactDocumentWipMutation({
    onCompleted: () => {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: `graph reset to latest committed document.`,
        status: 'success',
      });

      // refetch
      getStatus();
    },
    onError: (error) => {
      onError(error, 'resetArtifactDocumentWipToCurrent.onError');
    },
    refetchQueries: [
      {
        query: GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS,
        variables: { pipelineDefinitionId },
      },
    ],
  });

  const onResetToLatest = useCallback(() => {
    try {
      resetArtifactDocumentWipToCurrent({
        variables: {
          pipelineDefinitionId,
          artifactDocumentId: graph.artifactDocumentId,
        },
      });
    } catch (error) {
      onError(error, 'resetArtifactDocumentWipToCurrent');
    }
  }, [
    resetArtifactDocumentWipToCurrent,
    pipelineDefinitionId,
    graph.artifactDocumentId,
    onError,
  ]);

  const onGraphUploaded = useCallback(
    ({
      storedGraph,
      positions,
    }: {
      storedGraph: StoredGraphState;
      positions: GraphPositions;
    }) => {
      setGraph({
        ...graph,
        ...storedGraph,
        dirty: true,
      });
      updateFlowNodes(storedGraph, positions);
    },
    [graph, setGraph, updateFlowNodes],
  );

  const onMigrateXlsx2Csv = useCallback(() => {
    // eslint-disable-next-line no-console
    const { nodes, edges } = graph;
    const { graph: newGraph, errors } = migrateGraph({ nodes, edges });
    if (newGraph) {
      setGraph({
        ...graph,
        ...newGraph,
        dirty: true,
      });
      updateFlowNodes(newGraph, {});
    }

    if (errors.length) {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: errors.join(', '),
        status: 'info',
      });
    }
  }, [graph, setGraph, updateFlowNodes, toast]);

  const saveArtifactDocumentWip = useCallback(() => {
    try {
      const result = tapeBuilderGraphToArtifactsDocument({
        artifactDocumentId: graph.artifactDocumentId,
        graph,
        creditFacilities: graph.creditFacilities,
        loanProducts: graph.loanProducts,
      });

      if (result.isValid) {
        saveBorrowingBasePipelineArtifactsMutation({
          variables: {
            pipelineDefinitionId,
            artifactsDocumentString: result.artifactsDocument,
          },
        });
        saveNodePositions();
      } else {
        onError(result.errors.join(', '), 'saveArtifactDocumentWip');
      }
    } catch (error) {
      // any synchronous errors will be caught here.
      // but graphQL errors are caught in the
      // onError callback.
      onError(error, 'saveArtifactDocumentWip');
    }
  }, [
    saveBorrowingBasePipelineArtifactsMutation,
    saveNodePositions,
    graph,
    onError,
    pipelineDefinitionId,
  ]);

  const dbMessages: string[] = [];
  if (loadingStatus) {
    dbMessages.push('Loading Status...');
  }
  if (loadingArtifactInstances) {
    dbMessages.push('Loading Instances...');
  }
  if (loadingCurrentArtifactDocument) {
    dbMessages.push('Loading Artifacts...');
  }

  if (isResettingToCurrent) {
    dbMessages.push('Resetting wip back to current graph');
  }
  if (isSavingWip) {
    dbMessages.push('Saving Artifacts...');
  }

  if (savingNodePositions) {
    dbMessages.push('Saving Positions...');
  }

  const dbMessage = dbMessages.join(', ');
  const showGraph = !loadingCurrentArtifactDocument && !loadingStatus;

  const changeMode = (newMode: TapeBuilderMode) => {
    if (newMode !== 'Monitor') {
      // when switching away from monitor mode
      // clear artifactInstances
      setGraph({
        ...graph,
        artifactInstances: null,
      });
    }
    setMode(newMode);
  };

  // when currentArtifactDocumentId changes
  // fetch the updated artifacts document
  // for the current mode
  useEffect(() => {
    getCurrentArtifactsDocument(mode);
  }, [currentArtifactDocumentId, getCurrentArtifactsDocument, mode]);

  useEffect(() => {
    // when pipelineDefinitionId changes,
    // rerender the control with default state.
    setMode('Edit');
    setDiff(null);
  }, [pipelineDefinitionId]);

  useEffect(() => {
    // when mode changes remove diff control.
    setDiff(null);
  }, [mode]);

  const isGraphFiltered = graph.selectedTags.length > 0;

  return (
    <GraphProvider graph={graph} dispatch={dispatch}>
      <QAProvider
        qaMode={mode === 'QA'}
        pipelineDefinitionId={pipelineDefinitionId}
      >
        <TapeBuilderStatus
          mode={mode}
          status={status}
          pipelineDefinitionId={pipelineDefinitionId}
          dirty={graph.dirty}
          dbMessage={dbMessage}
          updateStatus={getStatus}
          onSaveWip={saveArtifactDocumentWip}
          setMode={changeMode}
          diff={diff}
          setDiff={setDiff}
          onLoadArtifactInstances={loadArtifactInstances}
          setArtifactDocumentId={setCurrentArtifactDocumentId}
          onClearGraph={clearGraph}
          onResetToLatest={onResetToLatest}
          onMigrateXlsx2Csv={onMigrateXlsx2Csv}
          onUndoChanges={() => {
            getStatus();
          }}
          isGraphFiltered={isGraphFiltered}
        />
        {diff && <TapeBuilderDiffViewer diff={diff} />}
        {showGraph && (
          <TapeBuilderControl
            mode={mode}
            pipelineDefinitionId={pipelineDefinitionId}
            orgSlugMap={orgSlugMap}
            onGraphUploaded={onGraphUploaded}
            handleSavePositions={saveNodePositions}
          />
        )}
      </QAProvider>
    </GraphProvider>
  );
};

export const TapeBuilderForPipelineDefinition = (props: Props) => {
  return (
    <ReactFlowProvider>
      <InternalComponent {...props} />
    </ReactFlowProvider>
  );
};
